import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { User, Mail, Calendar, MapPin, Ruler, Wallet, Edit3, Camera, X, Save, Instagram, Twitter, Shield, Swords, Eye, EyeOff, Crown } from "lucide-react";
import VipBadge from "@/components/app/VipBadge";
import AffiliatePanel from "@/components/app/AffiliatePanel";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [scans, setScans] = useState([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/scans/me").then((r) => setScans(r.data));
  }, []);

  const winRate = (user?.wins ?? 0) + (user?.losses ?? 0) === 0
    ? 0
    : Math.round((100 * (user?.wins ?? 0)) / ((user?.wins ?? 0) + (user?.losses ?? 0)));

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10">
      <div className="grid grid-cols-12 gap-6 mb-10">
        <div className="col-span-12 md:col-span-4 panel p-6 relative">
          <button
            onClick={() => setEditing(true)}
            className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest"
            data-testid="edit-profile-button"
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>

          <div className="w-28 h-28 border border-[var(--border-bright)] mb-4 overflow-hidden flex items-center justify-center bg-[var(--surface)]">
            {user?.avatar_base64 ? (
              <img src={`data:image/jpeg;base64,${user.avatar_base64}`} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar" />
            ) : (
              <User className="w-14 h-14 text-[var(--primary)]" />
            )}
          </div>

          <div className="font-display text-4xl leading-none flex items-center gap-3 flex-wrap">
            @{user?.username}
            <VipBadge user={user} size="md" />
          </div>
          {user?.vip_gifted_by && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)] mt-2" data-testid="profile-gifted-by">
              VIP gifted by @{user.vip_gifted_by}
            </div>
          )}
          {user?.display_name && (
            <div className="text-sm font-mono text-white mt-1" data-testid="profile-display-name">{user.display_name}</div>
          )}

          {user?.bio && (
            <p className="text-sm text-[var(--text-muted)] mt-4 leading-relaxed border-l-2 border-[var(--primary)] pl-3" data-testid="profile-bio">
              {user.bio}
            </p>
          )}

          <div className="mt-5 space-y-2 text-xs font-mono text-[var(--text-muted)]">
            <InfoRow icon={Mail} value={user?.email} />
            {user?.location && <InfoRow icon={MapPin} value={user.location} testid="profile-location" />}
            {user?.age && <InfoRow icon={Calendar} value={`${user.age} years old`} testid="profile-age" />}
            {user?.height_cm && <InfoRow icon={Ruler} value={`${user.height_cm} cm`} testid="profile-height" />}
            {user?.net_worth && <InfoRow icon={Wallet} value={user.net_worth} testid="profile-networth" />}
            <InfoRow icon={Calendar} value={`Joined ${user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}`} />
          </div>

          {(user?.instagram || user?.twitter) && (
            <div className="mt-5 pt-5 border-t border-[var(--border)] flex gap-3">
              {user.instagram && (
                <a href={`https://instagram.com/${user.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary)] flex items-center gap-1.5 text-xs font-mono" data-testid="profile-instagram">
                  <Instagram className="w-3.5 h-3.5" /> {user.instagram}
                </a>
              )}
              {user.twitter && (
                <a href={`https://twitter.com/${user.twitter.replace("@", "")}`} target="_blank" rel="noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary)] flex items-center gap-1.5 text-xs font-mono" data-testid="profile-twitter">
                  <Twitter className="w-3.5 h-3.5" /> {user.twitter}
                </a>
              )}
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] self-start">
          <StatBox label="ELO" value={user?.elo ?? 1000} accent />
          <StatBox label="BEST SCORE" value={user?.best_score ?? 0} />
          <StatBox label="WINS" value={user?.wins ?? 0} />
          <StatBox label="LOSSES" value={user?.losses ?? 0} />
          <StatBox label="WIN RATE" value={`${winRate}%`} />
          <StatBox label="SCANS" value={scans.length} />
          <StatBox label="LAST SCORE" value={scans[0]?.overall_score ?? "—"} />
          <StatBox label="LAST TIER" value={scans[0]?.analysis?.tier ?? "—"} />
        </div>
      </div>

      <div className="mb-10">
        <AffiliatePanel />
      </div>

      <div className="panel p-6">
        <span className="tape-label">FULL SCAN HISTORY</span>
        <div className="mt-6 space-y-px bg-[var(--border)]">
          {scans.length === 0 && (
            <div className="bg-[var(--surface)] p-6 text-center text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
              No scans yet — <Link to="/scan" className="text-[var(--primary)]" data-testid="profile-scan-link">run your first →</Link>
            </div>
          )}
          {scans.map((s) => (
            <Link
              key={s.id}
              to={`/scan/${s.id}`}
              className="bg-[var(--surface)] hover:bg-[var(--surface-2)] p-4 flex items-center justify-between transition-colors"
              data-testid={`profile-scan-${s.id}`}
            >
              <div className="flex items-center gap-4">
                <span className="font-display text-4xl text-white w-14">{s.overall_score}</span>
                <div>
                  <div className="text-sm font-mono text-white">{s.analysis?.tier ?? "Scan"} · {(s.mode || s.analysis?.mode || "face").toUpperCase()}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">{new Date(s.created_at).toLocaleString()}</div>
                </div>
              </div>
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">View →</span>
            </Link>
          ))}
        </div>
      </div>

      {editing && <EditProfileModal user={user} onClose={() => setEditing(false)} onSaved={refresh} />}
    </div>
  );
}

const InfoRow = ({ icon: Icon, value, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <Icon className="w-3 h-3" />
    <span>{value}</span>
  </div>
);

const StatBox = ({ label, value, accent }) => (
  <div className="bg-[var(--bg)] p-4">
    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);

function EditProfileModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    bio: user?.bio || "",
    net_worth: user?.net_worth || "",
    age: user?.age || "",
    height_cm: user?.height_cm || "",
    location: user?.location || "",
    instagram: user?.instagram || "",
    twitter: user?.twitter || "",
  });
  const [battleOptin, setBattleOptin] = useState(user?.battle_optin !== false);
  const [scansPrivate, setScansPrivate] = useState(user?.scans_private === true);
  const [avatarB64, setAvatarB64] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_base64 ? `data:image/jpeg;base64,${user.avatar_base64}` : null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAvatar = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use JPEG, PNG, or WEBP");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Avatar max 4MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result);
      setAvatarB64(reader.result.split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        if (k === "age" || k === "height_cm") {
          const n = parseInt(v);
          if (!isNaN(n)) payload[k] = n;
        } else {
          payload[k] = v;
        }
      });
      if (avatarB64) payload.avatar_base64 = avatarB64;
      // Always send privacy toggles (even if unchanged) so user can flip them
      payload.battle_optin = battleOptin;
      payload.scans_private = scansPrivate;
      await api.patch("/users/me", payload);
      toast.success("Profile updated.");
      await onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="panel-bright max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-profile-modal"
      >
        <div className="px-6 py-4 border-b border-[var(--border)] flex justify-between items-center sticky top-0 bg-[var(--surface-2)] z-10">
          <span className="tape-label">EDIT PROFILE</span>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid="edit-close-button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Avatar */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Profile Picture</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-[var(--border-bright)] bg-[var(--surface)] flex items-center justify-center overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="avatar preview" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-[var(--text-muted)]" />
                )}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="btn-ghost text-xs"
                data-testid="edit-avatar-button"
              >
                <Camera className="inline w-3.5 h-3.5 mr-1" /> Upload
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleAvatar(e.target.files[0])}
                data-testid="edit-avatar-input"
              />
            </div>
          </div>

          <Field label="Display Name" value={form.display_name} onChange={(v) => setField("display_name", v)} testid="edit-display-name" placeholder="e.g. John Ascender" />
          <Field label="Bio" value={form.bio} onChange={(v) => setField("bio", v)} textarea testid="edit-bio" placeholder="Mewing since 2022. Ascending daily." />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Age" value={form.age} onChange={(v) => setField("age", v)} type="number" testid="edit-age" />
            <Field label="Height (cm)" value={form.height_cm} onChange={(v) => setField("height_cm", v)} type="number" testid="edit-height" />
          </div>
          <Field label="Location" value={form.location} onChange={(v) => setField("location", v)} testid="edit-location" placeholder="LA, Tokyo, Berlin..." />
          <Field label="Net Worth" value={form.net_worth} onChange={(v) => setField("net_worth", v)} testid="edit-networth" placeholder="$50K · $1M · 7 figures" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Instagram" value={form.instagram} onChange={(v) => setField("instagram", v)} testid="edit-instagram" placeholder="@handle" />
            <Field label="Twitter / X" value={form.twitter} onChange={(v) => setField("twitter", v)} testid="edit-twitter" placeholder="@handle" />
          </div>

          {/* Privacy section */}
          <div className="pt-5 border-t border-[var(--border)]">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-[var(--primary)]" />
              <span className="tape-label">PRIVACY</span>
            </div>
            <ToggleRow
              icon={Swords}
              label="Include my photos in Mog Battles"
              hint="When OFF, your scans never appear in random 1v1 battles."
              value={battleOptin}
              onChange={setBattleOptin}
              testid="toggle-battle-optin"
            />
            <ToggleRow
              icon={scansPrivate ? EyeOff : Eye}
              label="Make my scans private"
              hint="When ON, only you can view your scans. They won't show up in battles either."
              value={scansPrivate}
              onChange={setScansPrivate}
              testid="toggle-scans-private"
            />
          </div>

          <div className="flex gap-3 pt-4 border-t border-[var(--border)]">
            <button onClick={save} disabled={saving} className="btn-brutal flex-1" data-testid="edit-save-button">
              <Save className="inline w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={onClose} className="btn-ghost" data-testid="edit-cancel-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ icon: Icon, label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[var(--border)] last:border-b-0">
      <div className="flex items-start gap-3 flex-1">
        <Icon className="w-4 h-4 text-[var(--primary)] mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-mono text-white">{label}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{hint}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        aria-pressed={value}
        className={`relative w-12 h-6 border transition-colors shrink-0 ${
          value
            ? "bg-[var(--primary)] border-[var(--primary)]"
            : "bg-[var(--surface)] border-[var(--border-bright)]"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-black transition-all ${
            value ? "left-[1.625rem]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", textarea, testid, placeholder }) => (
  <div>
    <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={300}
        className="brutal"
        data-testid={testid}
        placeholder={placeholder}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="brutal"
        data-testid={testid}
        placeholder={placeholder}
      />
    )}
  </div>
);
